import { useCallback, useEffect, useMemo, useState } from 'react';
import { safeLocalStorage } from '@/lib/safeStorage';

import {
  navigationItemMap,
  navigationItems,
  navigationPaths,
  type FlatNavigationItem,
} from '@/constants/navigation';

export const NAVIGATION_FAVORITES_KEY = 'stenpro:favorites';
const FAVORITES_EVENT = 'stenpro:favorites:update';

const sanitizeFavorites = (paths: string[]): string[] => {
  const seen = new Set<string>();
  const sanitized: string[] = [];

  paths.forEach((path) => {
    if (!navigationPaths.has(path) || seen.has(path)) {
      return;
    }

    seen.add(path);
    sanitized.push(path);
  });

  return sanitized;
};

const readStoredFavorites = (): string[] => {
  try {
    const raw = safeLocalStorage.getItem(NAVIGATION_FAVORITES_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    const asStrings = parsed.filter((value): value is string => typeof value === 'string');
    return sanitizeFavorites(asStrings);
  } catch (error) {
    console.warn('Kunde inte läsa favoriter från localStorage', error);
    return [];
  }
};

const writeFavorites = (paths: string[]) => {
  safeLocalStorage.setItem(NAVIGATION_FAVORITES_KEY, JSON.stringify(paths));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(FAVORITES_EVENT));
  }
};

export const useNavigationFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>(() => readStoredFavorites());

  const syncFavorites = useCallback(() => {
    setFavorites(readStoredFavorites());
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === NAVIGATION_FAVORITES_KEY) {
        syncFavorites();
      }
    };

    const handleCustomEvent = () => {
      syncFavorites();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(FAVORITES_EVENT, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(FAVORITES_EVENT, handleCustomEvent);
    };
  }, [syncFavorites]);

  const updateFavorites = useCallback((updater: (previous: string[]) => string[]) => {
    setFavorites((previous) => {
      const next = sanitizeFavorites(updater(previous));
      writeFavorites(next);
      return next;
    });
  }, []);

  const addFavorite = useCallback(
    (path: string) => {
      if (!navigationPaths.has(path)) {
        return;
      }

      updateFavorites((previous) => {
        if (previous.includes(path)) {
          return previous;
        }

        return [...previous, path];
      });
    },
    [updateFavorites],
  );

  const removeFavorite = useCallback(
    (path: string) => {
      updateFavorites((previous) => previous.filter((item) => item !== path));
    },
    [updateFavorites],
  );

  const toggleFavorite = useCallback(
    (path: string) => {
      updateFavorites((previous) =>
        previous.includes(path)
          ? previous.filter((item) => item !== path)
          : [...previous, path],
      );
    },
    [updateFavorites],
  );

  const isFavorite = useCallback(
    (path: string) => favorites.includes(path),
    [favorites],
  );

  const favoriteItems: FlatNavigationItem[] = useMemo(
    () =>
      favorites
        .map((path) => navigationItemMap.get(path))
        .filter((item): item is FlatNavigationItem => Boolean(item)),
    [favorites],
  );

  return {
    favorites,
    favoriteItems,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
  } as const;
};

export const useFavoriteNavigationItems = () => {
  const { favoriteItems } = useNavigationFavorites();
  return favoriteItems;
};

export const useAllNavigationItems = () => navigationItems;

